import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from './apiClient';
import type { AwsCredentialsForList } from './integrations/cloud/aws/credentials';
import type { AwsRole } from './integrations/cloud/aws/roles';
import type {
  K8SClustersData,
  K8SClusterStatus,
  UnconnectedK8SCluster,
} from './integrations/containers/k8sClusters';
import type { DatadogApiCredentialsForList } from './integrations/monitoring/datadog';
import type { GithubConfigForList } from './integrations/scm/github';
import type { GitlabConfigForList } from './integrations/scm/gitlab';
import type { S3Bucket } from './integrations/terraformBackend/buckets';
import type { HCPCloudTokenForList } from './integrations/terraformBackend/hcpCloud';
import type { GrafanaConfigurationForList } from './integrations/monitoring/grafana';
import type { GcpServiceAccountForList } from './gcpServiceAccount';
import type { ProjectMember } from './project';

export type GitlabWebhookStat = {
  configId: string;
  instanceUrl: string;
  webhookCount: number;
  webhooks: Array<{
    id: string;
    repositoryId: number;
    status: 'active' | 'pending' | 'error';
    createdAt: string;
  }>;
};

export type ProjectIntegrationsRaw = {
  projectName: string;
  id: string;
  neo4jDatabaseName: string;
  members: ProjectMember[];
  createdAt: string;
  updatedAt: string;
  awsCredentials: AwsCredentialsForList[];
  awsRoles: AwsRole[];
  hcpCloudTokens: HCPCloudTokenForList[];
  datadogApiCredentials: DatadogApiCredentialsForList[];
  githubInstallations: GithubConfigForList[];
  gitlabConfigurations: GitlabConfigForList[];
  gitlabWebhooks: GitlabWebhookStat[];
  grafana: GrafanaConfigurationForList[];
  gcpCredentials: GcpServiceAccountForList[];
  s3_buckets: S3Bucket[];
  k8sClusters?: K8SClustersData;
};

export type ProjectIntegrations = {
  projectName: string;
  id: string;
  neo4jDatabaseName: string;
  members: ProjectMember[];
  createdAt: string;
  updatedAt: string;
  awsCredentials: AwsCredentialsForList[];
  awsRoles: AwsRole[];
  hcpCloudTokens: HCPCloudTokenForList[];
  datadogApiCredentials: DatadogApiCredentialsForList[];
  githubInstallations: GithubConfigForList[];
  gitlabConfigurations: GitlabConfigForList[];
  gitlabWebhooks: GitlabWebhookStat[];
  grafana: GrafanaConfigurationForList[];
  gcpCredentials: GcpServiceAccountForList[];
  s3_buckets: S3Bucket[];
  k8sClusters: K8SClusterStatus[];
  unconnected_k8sClusters?: UnconnectedK8SCluster[];
};

const transformProjectIntegrations = (
  rawData: Record<string, ProjectIntegrationsRaw>,
): Record<string, ProjectIntegrations> => {
  const transformedData: Record<string, ProjectIntegrations> = {};

  for (const [key, project] of Object.entries(rawData)) {
    const k8sData = project.k8sClusters;
    const connected = k8sData?.connected ?? [];
    const discovered = k8sData?.discovered ?? [];

    transformedData[key] = {
      ...project,
      // Convert new structure to legacy format for existing display logic
      k8sClusters: connected.map(cluster => ({
        ...cluster,
        project_id: project.id,
      })),
      unconnected_k8sClusters: discovered.map(cluster => ({
        ...cluster,
        connected: false as const,
      })),
      // Pass through GitLab data as-is
      gitlabConfigurations: project.gitlabConfigurations,
      gitlabWebhooks: project.gitlabWebhooks,
    };
  }

  return transformedData;
};

export const adminQueries = {
  all: () => ['admin'],
  integrations: () =>
    queryOptions({
      queryKey: [...adminQueries.all(), 'integrations'],
      queryFn: async () => {
        const adminIntegrationsList = await fetchBackend(
          `/internal/admin/integrations`,
          {
            method: 'GET',
          },
        );

        if (!adminIntegrationsList.ok) {
          throw new Error(`Error: ${adminIntegrationsList.statusText}`);
        }

        const rawData = (await adminIntegrationsList.json()) as Record<
          string,
          ProjectIntegrationsRaw
        >;

        return transformProjectIntegrations(rawData);
      },
    }),
} as const;
